import type { Area } from "react-easy-crop";

import { useCallback, useState } from "react";
import Cropper from "react-easy-crop";

import { Button } from "@/components/button";
import {
  Dialog,
  DialogBody,
  DialogFooter,
  DialogHeader,
} from "@/components/dialog";
import { Modal } from "@/components/modal";

interface AvatarCropModalProps {
  imageSrc: string;
  isOpen: boolean;
  onApply: (blob: Blob) => void;
  onClose: () => void;
}

async function getCroppedBlob(
  imageSrc: string,
  pixelCrop: Area,
): Promise<Blob> {
  const image = await new Promise<HTMLImageElement>((resolve, reject) => {
    const img = new Image();

    img.addEventListener("load", () => resolve(img));
    img.addEventListener("error", reject);
    img.src = imageSrc;
  });

  const canvas = document.createElement("canvas");

  canvas.width = pixelCrop.width;
  canvas.height = pixelCrop.height;
  const ctx = canvas.getContext("2d")!;

  ctx.drawImage(
    image,
    pixelCrop.x,
    pixelCrop.y,
    pixelCrop.width,
    pixelCrop.height,
    0,
    0,
    pixelCrop.width,
    pixelCrop.height,
  );

  return new Promise((resolve, reject) => {
    canvas.toBlob((blob) => {
      if (blob) resolve(blob);
      else reject(new Error("Failed to create blob"));
    }, "image/png");
  });
}

export function AvatarCropModal({
  imageSrc,
  isOpen,
  onApply,
  onClose,
}: AvatarCropModalProps) {
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState<Area | null>(null);

  const onCropComplete = useCallback((_: Area, croppedPixels: Area) => {
    setCroppedAreaPixels(croppedPixels);
  }, []);

  const handleApply = useCallback(async () => {
    if (!croppedAreaPixels) return;
    const blob = await getCroppedBlob(imageSrc, croppedAreaPixels);

    onApply(blob);
  }, [croppedAreaPixels, imageSrc, onApply]);

  return (
    <Modal
      classNames={{ modal: "w-[480px]" }}
      isDismissable
      isOpen={isOpen}
      onOpenChange={(open) => {
        if (!open) onClose();
      }}
    >
      <Dialog>
        <DialogHeader>Edit photo</DialogHeader>
        <DialogBody className="pt-4">
          <div className="relative w-full h-72 bg-black rounded-lg overflow-hidden">
            <Cropper
              aspect={1}
              crop={crop}
              cropShape="round"
              image={imageSrc}
              onCropChange={setCrop}
              onCropComplete={onCropComplete}
              onZoomChange={setZoom}
              showGrid={false}
              zoom={zoom}
            />
          </div>
          <div className="mt-3 px-1">
            <input
              className="w-full accent-current"
              max={3}
              min={1}
              onChange={(e) => setZoom(Number(e.target.value))}
              step={0.05}
              type="range"
              value={zoom}
            />
          </div>
        </DialogBody>
        <DialogFooter>
          <Button color="gray" onPress={onClose} size="lg" variant="secondary">
            Cancel
          </Button>
          <Button onPress={handleApply} size="lg" variant="primary">
            Save
          </Button>
        </DialogFooter>
      </Dialog>
    </Modal>
  );
}
